/** Roboto Condensed font helpers for PDF and print document generation, matching the portal UI. */

export const REPORT_FONT = "'Roboto Condensed', sans-serif";
export const PDF_FONT = "RobotoCondensed";

const FONT_FILES = {
  regular: "RobotoCondensed-Regular.ttf",
  bold: "RobotoCondensed-Bold.ttf",
  medium: "RobotoCondensed-Medium.ttf",
  semiBold: "RobotoCondensed-SemiBold.ttf",
};

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
};

const getFontBasePath = () =>
  `${process.env.PUBLIC_URL || ""}/fonts`.replace(/\/+/g, "/");

let fontDataPromise = null;

const loadPdfFontData = () => {
  if (!fontDataPromise) {
    const basePath = getFontBasePath();

    fontDataPromise = Promise.all([
      fetch(`${basePath}/${FONT_FILES.regular}`).then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load ${FONT_FILES.regular}`);
        }
        return response.arrayBuffer();
      }),
      fetch(`${basePath}/${FONT_FILES.medium}`).then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load ${FONT_FILES.medium}`);
        }
        return response.arrayBuffer();
      }),
      fetch(`${basePath}/${FONT_FILES.semiBold}`).then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load ${FONT_FILES.semiBold}`);
        }
        return response.arrayBuffer();
      }),
      fetch(`${basePath}/${FONT_FILES.bold}`).then((response) => {
        if (!response.ok) {
          throw new Error(`Failed to load ${FONT_FILES.bold}`);
        }
        return response.arrayBuffer();
      }),
    ]).then(([regular, medium, semiBold, bold]) => ({
      regular: arrayBufferToBase64(regular),
      medium: arrayBufferToBase64(medium),
      semiBold: arrayBufferToBase64(semiBold),
      bold: arrayBufferToBase64(bold),
    }));
  }

  return fontDataPromise;
};

const hasPdfFont = (doc, fontName, style) => {
  const fontList = doc.getFontList?.() || {};
  const styles = fontList[fontName] || fontList[fontName.toLowerCase()];
  return Array.isArray(styles) && styles.includes(style);
};

const ensurePdfFont = (doc, fonts, fileName, fontName, style) => {
  if (hasPdfFont(doc, fontName, style)) return;

  doc.addFileToVFS(fileName, fonts[style === "bold" ? "bold" : "regular"]);
  doc.addFont(fileName, fontName, style);
};

const ensureNamedPdfFont = (doc, fonts, fileName, fontName) => {
  if (hasPdfFont(doc, fontName, "normal")) return;

  const key =
    fontName === "RobotoCondensedMedium"
      ? "medium"
      : fontName === "RobotoCondensedSemiBold"
        ? "semiBold"
        : "regular";

  doc.addFileToVFS(fileName, fonts[key]);
  doc.addFont(fileName, fontName, "normal");
};

/**
 * Register Roboto Condensed TTF files with a jsPDF instance.
 * Must run for every new jsPDF() document — fonts are not shared across instances.
 */
export const registerPdfFont = async (doc) => {
  const fonts = await loadPdfFontData();

  ensurePdfFont(doc, fonts, FONT_FILES.regular, PDF_FONT, "normal");
  ensurePdfFont(doc, fonts, FONT_FILES.bold, PDF_FONT, "bold");
  ensureNamedPdfFont(doc, fonts, FONT_FILES.medium, "RobotoCondensedMedium");
  ensureNamedPdfFont(doc, fonts, FONT_FILES.semiBold, "RobotoCondensedSemiBold");

  if (!hasPdfFont(doc, PDF_FONT, "normal")) {
    throw new Error("Roboto Condensed font failed to register in jsPDF");
  }

  doc.setFont(PDF_FONT, "normal");
};

/** Preload Roboto Condensed web fonts before html2canvas PDF capture. */
export const ensureReportFontsLoaded = async () => {
  const sizes = ["10px", "12px", "14px", "18px", "24px", "34px"];
  const weights = [400, 500, 600, 700];
  const loads = [];

  for (const size of sizes) {
    for (const weight of weights) {
      loads.push(document.fonts.load(`${weight} ${size} "Roboto Condensed"`));
    }
  }

  await Promise.all(loads);
  await document.fonts.ready;
};
